import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Hreflang } from "@/i18n/LanguageContext";

export default function TermsOfServiceNL() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page-nl">
      <Helmet>
        <title>Algemene Gebruiksvoorwaarden | DevisMaison.be</title>
        <meta name="description" content="Algemene gebruiksvoorwaarden van DevisMaison.be - Platform dat particulieren en aannemers met elkaar in contact brengt in België en Luxemburg." />
        <link rel="canonical" href="https://devismaison.be/nl/algemene-voorwaarden" />
      </Helmet>
      <Hreflang frPath="/conditions-generales" nlPath="/nl/algemene-voorwaarden" />
      <Navbar />

      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
            Algemene Gebruiksvoorwaarden
          </h1>

          <p className="text-slate-600 mb-8">
            Laatste update: {new Date().toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" })}
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 1 - Uitgever van de website
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 mb-4">
                <p className="text-slate-700 font-medium">Eigenaar van de website: Stéphane Muller (natuurlijke persoon)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant: Muller Construction SRL</p>
                <p className="text-slate-600">KBO / Btw-nr.: BE 0803.324.50</p>
                <p className="text-slate-600">Maatschappelijke zetel: Bertrix, België</p>
                <p className="text-slate-600">Telefoon: +32 61 46 83 32</p>
                <p className="text-slate-600">E-mail: contact@devismaison.be</p>
                <p className="text-slate-600">Website: www.devismaison.be</p>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Stéphane Muller behoudt zich als eigenaar van de website het recht voor om op elk moment van exploitant te veranderen, zonder dat dit de geldigheid van deze algemene voorwaarden aantast. Bij een wijziging van exploitant worden de gebruikers hiervan op de hoogte gebracht via een update van deze AGV.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 2 - Doel
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Deze algemene gebruiksvoorwaarden (AGV) hebben tot doel de modaliteiten en voorwaarden van het gebruik van het platform DevisMaison.be (hierna "het Platform"), eigendom van Stéphane Muller en geëxploiteerd door Muller Construction SRL, alsook de rechten en plichten van de gebruikers vast te leggen. Het Platform brengt particulieren die bouw- of renovatiewerken willen laten uitvoeren in contact met professionele aannemers.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 3 - Definities
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>"Platform"</strong>: de website DevisMaison.be en al zijn functionaliteiten</li>
                <li><strong>"Gebruiker"</strong>: elke persoon die toegang heeft tot het Platform</li>
                <li><strong>"Particulier"</strong>: natuurlijke persoon die een offerteaanvraag voor werken indient</li>
                <li><strong>"Aannemer"</strong>: bouwprofessional geregistreerd op het Platform om offerteaanvragen te ontvangen</li>
                <li><strong>"Lead"</strong>: gekwalificeerde offerteaanvraag ingediend door een Particulier</li>
                <li><strong>"Diensten"</strong>: alle diensten aangeboden door het Platform</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 4 - Aanvaarding van de voorwaarden
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Het gebruik van het Platform impliceert de volledige aanvaarding van deze AGV. Deze voorwaarden zijn toegankelijk op de website en hebben voorrang op elke andere versie of elk ander tegenstrijdig document. De Uitgever behoudt zich het recht voor deze voorwaarden op elk moment te wijzigen. De wijzigingen treden in werking zodra ze op het Platform worden gepubliceerd.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 5 - Aangeboden diensten
              </h2>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                5.1 Voor Particulieren
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Het Platform stelt Particulieren in staat om:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Gratis offerteaanvragen voor werken in te dienen</li>
                <li>Tot 5 offertes te ontvangen van gekwalificeerde Aannemers</li>
                <li>De aanbiedingen te vergelijken en vrij de Aannemer van hun keuze te selecteren</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                5.2 Voor Aannemers
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Het Platform stelt Aannemers in staat om:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Gratis een professioneel account aan te maken</li>
                <li>De offerteaanvragen te raadplegen die overeenkomen met hun activiteitsdomein</li>
                <li>Leads te kopen om toegang te krijgen tot de volledige contactgegevens van de Particulieren</li>
                <li>De Particulieren rechtstreeks te contacteren om hun diensten voor te stellen</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 6 - Registratie en Aannemersaccount
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                De registratie als Aannemer vereist:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Een wettelijk opgerichte onderneming in België of Luxemburg te zijn</li>
                <li>Over een geldig btw-nummer te beschikken</li>
                <li>Juiste en actuele informatie te verstrekken</li>
                <li>De professionele verplichtingen na te leven (verzekeringen, erkenningen, enz.)</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                De Aannemer is verantwoordelijk voor de vertrouwelijkheid van zijn inloggegevens en voor alle activiteiten die vanaf zijn account worden uitgevoerd.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 7 - Tarieven en betaling
              </h2>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.1 Gratis voor Particulieren
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Het gebruik van het Platform is volledig gratis voor Particulieren.
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.2 Tarieven voor Aannemers
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                De registratie en het raadplegen van leads zijn gratis. Het kopen van leads om toegang te krijgen tot de contactgegevens van de Particulieren is betalend. De tarieven worden bij elke lead vermeld en variëren naargelang de categorie van de werken.
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.3 Betaling
              </h3>
              <p className="text-slate-600 leading-relaxed">
                De betalingen gebeuren via SEPA-overschrijving. Bij de aankoop van een lead wordt een QR-code (Europese EPC-norm) gegenereerd om de overschrijving in uw bankapp vooraf in te vullen (begunstigde, IBAN, bedrag, mededeling). De contactgegevens van de Particulier worden meegedeeld zodra de betaling is ontvangen en gevalideerd. Er worden geen bankkaartgegevens verzameld. De prijzen worden vermeld in euro (EUR), inclusief btw voor Belgische en Luxemburgse ondernemingen.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 8 - Verplichtingen van de gebruikers
              </h2>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                8.1 Verplichtingen van de Particulieren
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Juiste en oprechte informatie verstrekken in de offerteaanvragen</li>
                <li>Geen fictieve of misleidende aanvragen indienen</li>
                <li>Binnen een redelijke termijn reageren op de verzoeken van de Aannemers</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                8.2 Verplichtingen van de Aannemers
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Juiste en actuele bedrijfsinformatie verstrekken</li>
                <li>De vertrouwelijkheid van de gegevens van de Particulieren respecteren</li>
                <li>De gekochte leads niet doorverkopen of delen</li>
                <li>De Particulieren op een professionele en respectvolle manier contacteren</li>
                <li>De in de offertes aangegane verbintenissen naleven</li>
                <li>Over de vereiste verzekeringen en erkenningen voor hun activiteit beschikken</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 9 - Aansprakelijkheid
              </h2>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                9.1 Rol van het Platform
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Het Platform treedt uitsluitend op als technische tussenpersoon die het contact tussen Particulieren en Aannemers vergemakkelijkt. Het is geen partij bij de tussen hen gesloten overeenkomsten en kan niet aansprakelijk worden gesteld voor:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>De kwaliteit, de prijs of de uitvoering van de werken</li>
                <li>Het gedrag van de gebruikers</li>
                <li>De juistheid van de door de gebruikers verstrekte informatie</li>
                <li>Geschillen tussen Particulieren en Aannemers</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                9.2 Beschikbaarheid
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Het Platform streeft naar een continue beschikbaarheid van zijn diensten maar kan geen permanente beschikbaarheid garanderen. Onderbrekingen voor onderhoud of in geval van overmacht zijn mogelijk.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 10 - Terugbetalingsbeleid
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Gekochte leads worden niet terugbetaald, behalve in de volgende gevallen:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Ongeldige of onjuiste contactgegevens van de Particulier (geverifieerd door onze dienst)</li>
                <li>Dubbele lead (hetzelfde project al gekocht)</li>
                <li>Kennelijk fictief of frauduleus project</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Elke terugbetalingsaanvraag moet binnen 7 dagen na de aankoop worden ingediend bij contact@devismaison.be met de gepaste bewijsstukken.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 11 - Intellectuele eigendom
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Alle elementen van het Platform (teksten, afbeeldingen, logo's, databank, software) zijn beschermd door intellectuele eigendomsrechten. Elke niet-toegestane reproductie, weergave of exploitatie is verboden en kan aanleiding geven tot vervolging.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 12 - Gegevensbescherming
              </h2>
              <p className="text-slate-600 leading-relaxed">
                De verwerking van persoonsgegevens wordt geregeld door ons <a href="/nl/privacybeleid" className="text-orange-500 hover:underline">Privacybeleid</a>, conform de AVG (GDPR) en de Belgische wet van 30 juli 2018.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 13 - Beëindiging
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                De Aannemer kan zijn account op elk moment afsluiten door contact op te nemen met onze klantendienst. Het Platform behoudt zich het recht voor een account te schorsen of te beëindigen in geval van:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Schending van deze AGV</li>
                <li>Frauduleus of misleidend gedrag</li>
                <li>Verstrekking van valse informatie</li>
                <li>Niet-naleving van de professionele verplichtingen</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 14 - Toepasselijk recht en bevoegde rechtbank
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Deze AGV worden beheerst door het Belgische recht. In geval van geschil zullen de partijen trachten een minnelijke oplossing te vinden. Bij gebrek daaraan zijn uitsluitend de rechtbanken van het gerechtelijk arrondissement Neufchâteau (België) bevoegd.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Artikel 15 - Contact
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Voor elke vraag over deze algemene voorwaarden:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Eigenaar van de website: Stéphane Muller (natuurlijke persoon)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant: Muller Construction SRL</p>
                <p className="text-slate-600">KBO / Btw-nr.: BE 0803.324.50</p>
                <p className="text-slate-600">E-mail: <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a></p>
                <p className="text-slate-600">Telefoon: +32 61 46 83 32</p>
                <p className="text-slate-600">Adres: Bertrix, België</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
